import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid } from '@material-ui/core';

import { Conditional } from '@klp/conditional';
import { useAppParams } from '@klp/core/app/useAppParams';
import { Block, Link, Typography } from '@klp/design';
import { useKlpStyles } from '@klp/design/Theme';

import type { Sak } from '../classes';
import type { SakForsidefeed } from '../classes/SakForsidefeed';
import { getSakHref } from '../helpers/SakHelpers';
import { useNySak } from '../hooks/useNySak';

const styles = () => ({
    container: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column' as const,
        flex: '1 1 auto',
        justifyContent: 'space-between' as const,
    },
    title: {
        textTransform: 'uppercase' as const,
    },
    chip: {
        marginLeft: '0.25rem',
    },
    line: {
        display: 'flex',
        paddingTop: '0.25rem',
        paddingBottom: '0.25rem',
        '&:first-of-type': {
            marginTop: '-0.25rem',
        },
    },
});

interface IProps {
    sakFeed: SakForsidefeed;
}

export type ISakListSummary = IProps;

export const SakListSummary: React.FC<ISakListSummary> = (props) => {
    const { t } = useTranslation();
    const { sakFeed } = props;
    const classes = useKlpStyles({}, styles);
    const { selectedClient } = useAppParams();
    const {
        nySakHref,
        isDalux: nySakIsDalux,
        isLoading: nySakLoading,
    } = useNySak({
        kundeNummer: selectedClient?.kundeNummer,
    });

    const sakList = sakFeed?.issues || [];

    const renderSakRow = (sak: Sak) => (
        <Block
            key={sak.id}
            klpTypographyVariant="xsmall"
            className={classes.line}
        >
            <Link
                href={getSakHref(sak)}
                target={sak.isDalux ? '_blank' : undefined}
                klpTypographyVariant="xsmall"
            >
                {sak.emne}
            </Link>
        </Block>
    );

    return (
        <Block className={classes.container}>
            <Block>
                <Conditional
                    condition={sakList && sakList.length > 0}
                    /* eslint-disable-next-line react/jsx-no-useless-fragment */
                    errorComponent={<Fragment />}
                >
                    <Grid container={true}>
                        <Grid item={true} xs={12}>
                            <Block marginTop="compact">
                                {sakList && sakList?.length > 0 && (
                                    <Typography
                                        klpTypographyVariant="xsmall"
                                        className={classes.title}
                                    >
                                        {t('sak.summarytitle', {
                                            count: sakList?.length,
                                        })}
                                    </Typography>
                                )}
                                {sakList?.map((sak: Sak) => renderSakRow(sak))}
                            </Block>
                        </Grid>
                    </Grid>
                </Conditional>
            </Block>
            <Block>
                <Link
                    href={nySakHref}
                    target={nySakIsDalux ? '_blank' : undefined}
                    disabled={nySakLoading}
                    translation="superOffice.issue.new"
                />
            </Block>
        </Block>
    );
};

SakListSummary.displayName = 'SakListSummary';
